import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Star, MapPin, Languages } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Guides() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    area: '',
    language: '',
    specialty: ''
  });

  useEffect(() => {
    fetchGuides();
  }, [filters]);

  const fetchGuides = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.area) params.append('area', filters.area);
      if (filters.language) params.append('language', filters.language);
      if (filters.specialty) params.append('specialty', filters.specialty);
      
      const response = await axios.get(`${API_URL}/api/guides?${params.toString()}`);
      setGuides(response.data);
    } catch (error) {
      console.error('Error fetching guides:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12" data-testid="guides-page">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-12">
          <h1 
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            {t('pages.hosts')}
          </h1>
          <p className="text-xl text-zinc-400">{t('pages.discover')}</p>
        </div>

        {/* Filters */}
        <Card className="bg-zinc-900/50 border-white/10 p-6 mb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select value={filters.area} onValueChange={(value) => setFilters({...filters, area: value})}>
              <SelectTrigger className="bg-black/20 border-white/10 text-white">
                <SelectValue placeholder={t('search.area')} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">{t('pages.allAreas')}</SelectItem>
                <SelectItem value="Shibuya">{t('search.areas.shibuya')}</SelectItem>
                <SelectItem value="Shinjuku">{t('search.areas.shinjuku')}</SelectItem>
                <SelectItem value="Asakusa">{t('search.areas.asakusa')}</SelectItem>
                <SelectItem value="Harajuku">{t('search.areas.harajuku')}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.language} onValueChange={(value) => setFilters({...filters, language: value})}>
              <SelectTrigger className="bg-black/20 border-white/10 text-white">
                <SelectValue placeholder={t('search.language')} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">{t('pages.allLanguages')}</SelectItem>
                <SelectItem value="fr">{t('search.languages.fr')}</SelectItem>
                <SelectItem value="en">{t('search.languages.en')}</SelectItem>
                <SelectItem value="jp">{t('search.languages.jp')}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.specialty} onValueChange={(value) => setFilters({...filters, specialty: value})}>
              <SelectTrigger className="bg-black/20 border-white/10 text-white">
                <SelectValue placeholder={t('search.theme')} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">{t('pages.allThemes')}</SelectItem>
                <SelectItem value="food">{t('search.themes.food')}</SelectItem>
                <SelectItem value="culture">{t('search.themes.culture')}</SelectItem>
                <SelectItem value="nightlife">{t('search.themes.nightlife')}</SelectItem>
                <SelectItem value="shopping">{t('search.themes.shopping')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Guides Grid */}
        {loading ? (
          <div className="text-center text-white text-xl py-20">{t('common.loading')}</div>
        ) : guides.length === 0 ? (
          <div className="text-center text-zinc-400 text-xl py-20">{t('pages.noHosts')}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {guides.map((guide) => (
              <Card
                key={guide.guide_id}
                className={'group relative overflow-hidden rounded-2xl bg-zinc-900/50 border transition-all duration-500 cursor-pointer '
                  + (guide.featured
                      ? 'border-[#D4AF37]/70 shadow-[0_0_24px_rgba(212,175,55,0.18)] hover:border-[#D4AF37]'
                      : 'border-white/10 hover:border-[#F9004D]/50')}
                onClick={() => navigate(`/guides/${guide.guide_id}`)}
                data-testid={`guide-card-${guide.guide_id}`}
              >
                {/* Mise en avant : la forme reste identique, seul un anneau doré
                    et un badge explicite distinguent le guide (une différence de
                    forme se lirait comme un défaut d'affichage). */}
                {guide.featured && (
                  <div className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full bg-[#D4AF37] text-black text-xs font-bold shadow-lg">
                    {guide.featured === 'creator' ? t('host.featuredCreator') : t('host.featuredStar')}
                  </div>
                )}
                {/* Guide Photo */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={guide.cover_photo_url || guide.profile_photo_url || guide.user_picture || 'https://images.unsplash.com/photo-1578656161729-a8f77b7ffd4c?w=400'}
                    alt={guide.user_name}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  
                  {/* Rating Badge */}
                  {guide.average_rating > 0 && (
                    <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md rounded-full px-3 py-1 flex items-center gap-1">
                      <Star className="h-4 w-4 fill-[#D4AF37] text-[#D4AF37]" />
                      <span className="text-white font-bold">{guide.average_rating.toFixed(1)}</span>
                    </div>
                  )}
                </div>

                <div className="p-6 space-y-4">
                  {/* Guide Info */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={guide.profile_photo_url || guide.user_picture} alt={guide.user_name} />
                        <AvatarFallback className="bg-[#F9004D] text-white">
                          {guide.user_name?.[0] || 'G'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                          {guide.user_name}
                        </h3>
                        <p className="text-sm text-zinc-400">
                          {guide.total_reviews} {t('host.reviews')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Bio */}
                  <p className="text-zinc-300 text-sm line-clamp-2">
                    {guide.bio?.[i18n.language] || guide.bio?.en || 'Passionate Tokyo guide'}
                  </p>

                  {/* Language levels */}
                  {(() => {
                    const langs = [
                      { key: 'fr', flag: '🇫🇷', label: 'Français' },
                      { key: 'en', flag: '🇬🇧', label: 'English' },
                      { key: 'jp', flag: '🇯🇵', label: '日本語' },
                    ].filter(({ key }) => (guide.language_levels?.[key] || 0) > 0);
                    if (langs.length === 0) return null;
                    return (
                      <div className="flex gap-2">
                        {langs.map(({ key, flag, label }) => {
                          const lvl = guide.language_levels?.[key] || 0;
                          const pct = Math.round((lvl / 7) * 100);
                          const levelText = lvl <= 2 ? t('becomeHost.levels.beginner') : lvl <= 4 ? t('becomeHost.levels.intermediate') : lvl <= 5 ? t('becomeHost.levels.advanced') : lvl === 6 ? t('becomeHost.levels.fluent') : t('becomeHost.levels.native');
                          const color = lvl <= 2 ? '#3B82F6' : lvl <= 4 ? '#F59E0B' : '#10B981';
                          return (
                            <div key={key} className="flex-1 flex flex-col items-center text-center p-3 rounded-xl"
                              style={{ background: `${color}18`, border: `1.5px solid ${color}55` }}>
                              <span className="text-2xl leading-none mb-2">{flag}</span>
                              <span className="text-[11px] font-bold text-white mb-1 truncate w-full text-center">{label}</span>
                              <span className="text-[10px] font-semibold mb-2" style={{ color }}>{levelText}</span>
                              <div className="w-full h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                                <div className="h-full rounded-full"
                                  style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}70, ${color})` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* View Profile Button */}
                  <div className="flex items-center justify-end pt-4 border-t border-white/10">
                    <Button
                      className="bg-[#F9004D] hover:bg-[#D00040] text-white rounded-full px-6 py-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/guides/${guide.guide_id}`);
                      }}
                    >
                      {t('host.viewProfile')}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}